import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Image, 
  Briefcase, 
  FileText, 
  MessageSquare, 
  Settings, 
  LogOut,
  Menu,
  X,
  Users,
  Key,
  Activity,
  Shield,
  ShieldCheck,
  ShieldAlert,
  FileEdit
} from 'lucide-react';

const AdminLayout = ({ children }) => {
  const { logout, user, canAccess, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <ShieldAlert className="w-4 h-4 text-red-400" />;
      case 'hr_admin': return <ShieldCheck className="w-4 h-4 text-blue-400" />;
      default: return <Shield className="w-4 h-4 text-green-400" />;
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'hr_admin': return 'HR Admin';
      default: return 'Editor';
    }
  };

  // Define menu items with permission requirements
  const allMenuItems = [
    { path: '/admin/dashboard', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard', show: true },
    { path: '/admin/content', icon: <FileEdit className="w-5 h-5" />, label: 'Page Content', show: canAccess('settings') },
    { path: '/admin/jobs', icon: <Briefcase className="w-5 h-5" />, label: 'Job Listings', show: canAccess('jobs') },
    { path: '/admin/applications', icon: <FileText className="w-5 h-5" />, label: 'Applications', show: canAccess('applications') },
    { path: '/admin/gallery', icon: <Image className="w-5 h-5" />, label: 'Gallery', show: canAccess('gallery') },
    { path: '/admin/messages', icon: <MessageSquare className="w-5 h-5" />, label: 'Messages', show: true },
    { path: '/admin/settings', icon: <Settings className="w-5 h-5" />, label: 'Company Info', show: canAccess('settings') },
    { path: '/admin/users', icon: <Users className="w-5 h-5" />, label: 'User Management', show: canAccess('users') },
    { path: '/admin/audit-logs', icon: <Activity className="w-5 h-5" />, label: 'Audit Logs', show: hasPermission('audit.view') },
    { path: '/admin/change-password', icon: <Key className="w-5 h-5" />, label: 'Change Password', show: true },
  ];

  const menuItems = allMenuItems.filter(item => item.show);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className={`bg-gray-900 text-white transition-all duration-300 flex flex-col ${isSidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-800">
          {isSidebarOpen && (
            <div className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_lehigh-hss-updated/artifacts/pdbn1to4_image.png"
                alt="Lehigh HSS"
                className="h-10 w-auto"
              />
            </div>
          )}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
            data-testid="toggle-sidebar"
          >
            {isSidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-amber-500 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {item.icon}
                {isSidebarOpen && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-800">
          {isSidebarOpen && user && (
            <div className="mb-4 px-4 py-3 bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                {getRoleIcon(user.role)}
                <span className="text-xs text-gray-400">{getRoleName(user.role)}</span>
              </div>
              <p className="text-sm font-medium truncate">{user.full_name || user.username}</p>
              <p className="text-xs text-gray-400 truncate">{user.email}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {isSidebarOpen && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name || user?.username || 'Admin'}</p>
                <div className="flex items-center justify-end gap-1">
                  {user && getRoleIcon(user.role)}
                  <p className="text-xs text-gray-500">{user ? getRoleName(user.role) : ''}</p>
                </div>
              </div>
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white font-bold">
                {(user?.username || 'A')[0].toUpperCase()}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
